import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Database, FileSearch, Mail } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BrandMark } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function LoginPage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [needsSetup, setNeedsSetup] = useState(false);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);
  useEffect(() => {
    api.get("/auth/setup-status").then((r) => setNeedsSetup(r.data.needs_setup)).catch(() => {});
  }, []);
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const requestCode = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/request-code", { email: email.trim() });
      if (data.delivery === "delayed") toast.warning(data.message, { duration: 12000 }); else toast.success(data.message);
      setStep("code");
      setCode("");
      setCooldown(data.delivery === "delayed" ? (data.retry_after_seconds || 60) : 60);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-code", { email: email.trim(), code: code.trim() });
      setUser(data);
      toast.success(`Signed in as ${data.email}`);
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <section className="hidden lg:flex flex-col justify-between bg-[#101C47] tqi-grain p-12 text-white">
        <BrandMark light size="lg" />
        <div className="max-w-lg tqi-rise">
          <p className="tqi-eyebrow text-white/60">Internal operations platform</p>
          <h1 className="mt-3 text-4xl font-extrabold tracking-tight leading-tight">EZLynx feed ingestion, exception surveillance and operational reporting.</h1>
          <ul className="mt-8 space-y-4 text-sm text-white/80">
            <li className="flex gap-3"><Database className="h-5 w-5 text-[#F92626] shrink-0" />Four scheduled feeds — open and closed activities, new business, cancellations — archived, validated and deduplicated before anything is counted.</li>
            <li className="flex gap-3"><FileSearch className="h-5 w-5 text-[#F92626] shrink-0" />Every metric carries its reporting period, source freshness and definition. Missing inputs show as Unavailable, never as zero.</li>
            <li className="flex gap-3"><ShieldCheck className="h-5 w-5 text-[#F92626] shrink-0" />EZLynx stays read-only. Reviews and resolutions are recorded here with an audit trail, never written back.</li>
          </ul>
        </div>
        <p className="text-xs text-white/50">Access is limited to individually approved staff. Sign-in codes are single-use and expire after 10 minutes.</p>
      </section>
      <section className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <div className="w-full max-w-md tqi-rise">
          <div className="lg:hidden mb-8"><BrandMark size="lg" /></div>
          {needsSetup && (
            <div className="mb-6 rounded-md border border-[#F92626]/40 bg-[#FEF2F2] px-4 py-3 text-sm text-[#101C47]" data-testid="setup-banner">
              <span className="font-bold">No administrator is configured yet.</span> <Link to="/setup" className="underline font-semibold" data-testid="setup-link">Run the protected setup</Link> to create the first admin.
            </div>
          )}
          <h2 className="text-2xl font-extrabold tracking-tight text-[#101C47]">{step === "email" ? "Sign in" : "Enter your code"}</h2>
          <p className="mt-1 text-sm text-slate-600">{step === "email" ? "Enter your approved work email. We will send a six-digit sign-in code." : <>A six-digit code was sent to <span className="font-semibold">{email}</span> if that address is approved.</>}</p>
          {step === "email" ? (
            <form onSubmit={requestCode} className="mt-8 space-y-4" data-testid="login-email-form">
              <div className="space-y-2">
                <Label htmlFor="email">Email address</Label>
                <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@totalqualityinsurance.com" data-testid="login-email-input" />
              </div>
              <Button type="submit" disabled={busy || !email} className="w-full bg-[#F92626] hover:bg-[#E01C1C] text-white font-semibold" data-testid="login-submit-email-button"><Mail className="mr-2 h-4 w-4" />{busy ? "Sending…" : "Send sign-in code"}</Button>
            </form>
          ) : (
            <form onSubmit={verify} className="mt-8 space-y-4" data-testid="login-code-form">
              <div className="space-y-2">
                <Label htmlFor="code">Six-digit code</Label>
                <Input id="code" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} autoComplete="one-time-code" required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="••••••" className="font-mono text-2xl tracking-[0.6em] text-center h-14" data-testid="login-code-input" />
              </div>
              <Button type="submit" disabled={busy || code.length !== 6} className="w-full bg-[#101C47] hover:bg-[#1B2D6E] text-white font-semibold" data-testid="login-submit-code-button">{busy ? "Verifying…" : "Sign in"}</Button>
              <div className="flex items-center justify-between text-sm">
                <button type="button" className="text-slate-600 hover:text-[#101C47] underline" onClick={() => setStep("email")} data-testid="login-change-email-button">Use a different email</button>
                <button type="button" disabled={cooldown > 0 || busy} className="font-semibold text-[#101C47] disabled:text-slate-400" onClick={requestCode} data-testid="login-resend-code-button">{cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}</button>
              </div>
            </form>
          )}
          <p className="mt-8 text-xs text-slate-500">Codes are single-use, expire after 10 minutes and allow five attempts. Requesting a new code invalidates the previous one. This is email-code sign-in, not multi-factor authentication.</p>
        </div>
      </section>
    </div>
  );
}
